/**
 * "Away" — someone on leave or out sick (Backend-Development
 * docs/features/task-assignment/desk-and-away-contract.md §1, §6).
 *
 * While a person is marked away the system gives them no new tasks, a manager cannot hand them
 * one, and within 10 minutes the open tasks they hold are passed on. They mark themselves away
 * from the user menu ("Mark me as away" / "I'm back"); a team's manager or an Admin can do it for
 * them from Settings → Teams (the members list) and Settings → Users.
 *
 * The server decides who may change it (`PUT /api/v1/users/{userId}/away`); the screens only
 * decide where the switch is drawn. An older server sends no `away` at all, and then nothing new
 * shows anywhere.
 *
 * Kept free of JSX and of the stores so the rules can be tested on their own (`away.test.ts`).
 */

import { format, isValid, parseISO } from 'date-fns'
import type { authFetch } from '@/lib/api'

/** The three fields the server sends on a user (login, `/auth/me`, the user lists). */
export interface AwayFields {
  away?: boolean | null
  /** ISO instant; null when not away. */
  awaySince?: string | null
  /** userId of whoever marked them away; null when not away. */
  awaySetBy?: string | null
}

/** True or false when the server said; null when it said nothing (an older server). */
export function awayStateOf(person: AwayFields | null | undefined): boolean | null {
  const away = person?.away
  return typeof away === 'boolean' ? away : null
}

/** Marked away. A missing field reads as not away. */
export function isMarkedAway(person: AwayFields | null | undefined): boolean {
  return person?.away === true
}

/** The chip's tooltip: "Away since 20 Oct", or just "Away" when the date is not known. */
export function awayChipTitle(since: string | null | undefined): string {
  if (!since) return 'Away'
  const date = parseISO(since)
  return isValid(date) ? `Away since ${format(date, 'd MMM')}` : 'Away'
}

// ─────────────────────────────────────────────────────────────────────────────
// The user menu
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The user menu's item for the signed-in person: "Mark me as away" or "I'm back", with the value
 * it sets — or null when the server has not said whether they are away (an older server), which
 * also means the endpoint is not there to call.
 */
export function selfAwayMenuItem(me: AwayFields | null | undefined): { label: string; next: boolean } | null {
  const state = awayStateOf(me)
  if (state === null) return null
  return state ? { label: "I'm back", next: false } : { label: 'Mark me as away', next: true }
}

/** What the toast says after a change. */
export function awayToastText(next: boolean, target: { self: boolean; name?: string | null }): string {
  if (target.self) {
    return next ? "You're marked away. Your open tasks will be passed on within 10 minutes." : 'Welcome back'
  }
  const name = target.name?.trim() || 'They'
  return next
    ? `${name} is marked away. Their open tasks will be passed on within 10 minutes.`
    : `${name} can take tasks again`
}

// ─────────────────────────────────────────────────────────────────────────────
// Who sees the switch for someone else
// ─────────────────────────────────────────────────────────────────────────────

/** The signed-in person, as the screens know them (the auth store's user). */
export interface AwayViewer {
  userId: string
  /** The legacy role enum (ADMIN | MANAGER | EXECUTIVE | VIEWER). */
  role?: string | null
  roleId?: string | null
  roleName?: string | null
}

/** A team, as Settings → Teams has it. */
export interface AwayTeam {
  name: string
  managerIds?: readonly string[] | null
  isActive?: boolean | null
}

/**
 * The Admin role. The server tests the role name the gateway forwards ("ADMIN"); the screen
 * accepts the role id or its display name too, which only ever draws a switch the server then
 * still decides on.
 */
export function isAdminViewer(viewer: AwayViewer | null | undefined): boolean {
  if (!viewer) return false
  const upper = (value: string | null | undefined) => value?.trim().toUpperCase() ?? ''
  return upper(viewer.role) === 'ADMIN' || upper(viewer.roleName) === 'ADMIN' || viewer.roleId === 'role_admin'
}

function managesTeam(viewer: AwayViewer, team: AwayTeam): boolean {
  return team.isActive !== false && (team.managerIds ?? []).includes(viewer.userId)
}

/** Settings → Teams, one team's members: an Admin, or a manager of that (active) team. */
export function mayToggleAwayInTeam(viewer: AwayViewer | null | undefined, team: AwayTeam | null | undefined): boolean {
  if (!viewer || !team) return false
  return isAdminViewer(viewer) || managesTeam(viewer, team)
}

/**
 * Settings → Users, one person: an Admin, or a manager of an active team the person is in (by
 * team name, as the server matches it). A deactivated person cannot be marked away, so their
 * switch is only drawn to switch an old mark off.
 */
export function mayToggleAwayForUser(
  viewer: AwayViewer | null | undefined,
  person: AwayFields & { userId: string; teams?: readonly string[] | null; isActive?: boolean | null },
  teams: readonly AwayTeam[],
): boolean {
  if (!viewer) return false
  if (person.isActive === false && !isMarkedAway(person)) return false
  if (isAdminViewer(viewer)) return true
  const theirs = new Set(person.teams ?? [])
  return teams.some((team) => theirs.has(team.name) && managesTeam(viewer, team))
}

// ─────────────────────────────────────────────────────────────────────────────
// Refusals
// ─────────────────────────────────────────────────────────────────────────────

export type AwayRefusal = 'NOT_ALLOWED' | 'USER_INACTIVE'

/** The refusal an error carries (in `data.reason` or `details.reason`), or null. */
export function awayRefusalOf(err: unknown): AwayRefusal | null {
  if (!err || typeof err !== 'object' || !('data' in err)) return null
  const body = (err as { data?: unknown }).data
  if (!body || typeof body !== 'object') return null
  const { details, data } = body as { details?: { reason?: unknown } | null; data?: { reason?: unknown } | null }
  const reason = details?.reason ?? data?.reason
  return reason === 'NOT_ALLOWED' || reason === 'USER_INACTIVE' ? reason : null
}

/** A failed change, in plain words: a title, and the server's own sentence under it. */
export function awayErrorCopy(err: unknown, next: boolean): { title: string; description: string } {
  const message = err instanceof Error && err.message.trim() ? err.message.trim() : 'Please try again.'
  switch (awayRefusalOf(err)) {
    case 'NOT_ALLOWED':
      return { title: "You can't change this for them", description: message }
    case 'USER_INACTIVE':
      return { title: 'This account is deactivated', description: message }
    default:
      return { title: next ? "Couldn't mark as away" : "Couldn't mark as back", description: message }
  }
}

/** Any 4xx: the screen was out of date (or the viewer may not), so it re-reads. */
function isRefusal(err: unknown): boolean {
  const status = err && typeof err === 'object' ? (err as { status?: unknown }).status : undefined
  return typeof status === 'number' && status >= 400 && status < 500
}

// ─────────────────────────────────────────────────────────────────────────────
// The call
// ─────────────────────────────────────────────────────────────────────────────

/** `authFetch`'s shape, so a test can pass its own. */
export type AwayFetch = <T>(endpoint: string, token: string, options?: Parameters<typeof authFetch>[2]) => Promise<T>

/**
 * PUT /api/v1/users/{userId}/away {away}. Returns the person's away fields as now stored. The
 * global 403 toast is skipped: the control that asked says the refusal once, in plain words.
 */
export async function requestSetAway(fetcher: AwayFetch, token: string, userId: string, away: boolean): Promise<AwayFields> {
  const response = await fetcher<{ success: boolean; data?: AwayFields | null; message?: string }>(
    `/api/v1/users/${encodeURIComponent(userId)}/away`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ away }),
      suppressPermissionToast: true,
    },
  )
  if (!response.success) {
    throw new Error(response.message || (away ? 'Could not mark as away' : 'Could not mark as back'))
  }
  const data = response.data ?? {}
  return { away: typeof data.away === 'boolean' ? data.away : away, awaySince: data.awaySince ?? null, awaySetBy: data.awaySetBy ?? null }
}

/** How an attempt ended: done; refused by the server (re-read); or failed (try again). */
export type AwayOutcome = 'done' | 'refused' | 'failed'

export interface AwayNotifier {
  success: (title: string) => void
  error: (title: string, options: { description: string }) => void
}

export interface AwayChangeDeps {
  request: (userId: string, away: boolean) => Promise<AwayFields>
  /** The signed-in person's userId: a change to them is "you", and updates their cached record. */
  myUserId: string | null | undefined
  /** Put the signed-in person's new away fields into the app's cached record of them. */
  applyToMe: (fields: AwayFields) => void
  /** Re-read the cached user records (the signed-in person's when `self`, and the user lists). */
  refreshCaches: (self: boolean) => void
  notify: AwayNotifier
}

/**
 * Mark `target` away (`next` true) or back, say how it went, and refresh what the app caches
 * about users. A refusal (any 4xx) also refreshes — the screen was out of date — and the caller
 * re-reads its own list; a network failure or a server fault leaves everything as it was.
 */
export async function changeAway(
  deps: AwayChangeDeps,
  target: { userId: string; name?: string | null },
  next: boolean,
): Promise<AwayOutcome> {
  const self = !!deps.myUserId && target.userId === deps.myUserId
  try {
    const fields = await deps.request(target.userId, next)
    if (self) deps.applyToMe(fields)
    deps.notify.success(awayToastText(next, { self, name: target.name }))
    deps.refreshCaches(self)
    return 'done'
  } catch (err) {
    const copy = awayErrorCopy(err, next)
    deps.notify.error(copy.title, { description: copy.description })
    if (isRefusal(err)) {
      deps.refreshCaches(self)
      return 'refused'
    }
    return 'failed'
  }
}
